import type { components } from '../../api/generated/schema'

type Schema = components['schemas']

/**
 * The typed section map for the campaign sheet — the second of the six the
 * implementation plan §2.2 warned about.
 *
 * **Every one of the seven is hand-written here**, which is worse than the
 * character sheet's four-of-fourteen. The reason is the same and the cause is
 * structural: `CampaignSheetDto.Sections` and `CampaignEnvelope.Changed` are both
 * `IReadOnlyDictionary<string, object>`, and — unlike the character sections, ten
 * of which `EncounterCardDto` happens to reference as real properties — *nothing*
 * in the API mentions `CampaignCoreSectionDto` and its siblings from an operation.
 * So OpenAPI never sees them and `openapi-typescript` cannot emit them.
 *
 * They are transcribed from
 * `CampaignManager.Api/Contracts/Campaigns/SectionDtos.cs`, and the CI drift check
 * cannot catch a change to any of them. The captured fixture and the shape
 * assertions in `sections.test.tsx` are their entire safety net. Making them
 * reachable from an operation would delete this whole block.
 *
 * The *enums* below are generated, and stay that way — `MemberRole`,
 * `RelationshipType` and `CharacterType` all appear on request bodies.
 */
export interface CampaignSections {
  core: CampaignCoreSection
  players: PlayersSection
  characters: CampaignCharactersSection
  notes: NotesSection
  city: CitySection
  factions: FactionsSection
  locations: LocationsSection
}

// ---------------------------------------------------------------------------
// Hand-transcribed sections. Keep in step with SectionDtos.cs.
// ---------------------------------------------------------------------------

/**
 * Campaign identity plus the counts and capability flags a client would otherwise
 * derive. Section key `core`.
 */
export interface CampaignCoreSection {
  id: number
  name: string
  gameMaster: Participant
  /** Null when the campaign has no city yet — the signal to offer "create city". */
  cityId: number | null
  cityName: string | null
  /** GM + players, so the client never has to remember the GM is not a membership row. */
  participantCount: number
  characterCount: number
  /**
   * Whether *this caller* may still attach a character: always true for the GM, true
   * for a player only while they have no PC here. Server-computed, so the button is
   * shown on the server's word rather than on a rule re-implemented in the client.
   */
  canAddCharacter: boolean
  createdAt: string
}

/** One participant. The GM appears here too, flagged. */
export interface Participant {
  userId: string
  displayName: string | null
  isGameMaster: boolean
  joinedAt: string | null
}

/** Everyone in the campaign, GM first. Section key `players`. */
export interface PlayersSection {
  participants: Participant[]
}

/** A character on the campaign roster, with its portrait already resolved to a URL. */
export interface CampaignCharacter {
  characterId: number
  name: string
  type: Schema['CharacterType']
  ownerId: string
  ownerName: string | null
  portraitUrl: string | null
  addedAt: string
}

/**
 * The campaign roster. Section key `characters`. `Temp` characters are filtered out
 * for non-GMs server-side — never a client-side flag.
 */
export interface CampaignCharactersSection {
  characters: CampaignCharacter[]
}

/** A campaign note. Notes are private to their owner, reads included. */
export interface Note {
  id: number
  title: string
  content: string
  createdAt: string
  updatedAt: string
}

/** The caller's own notes for this campaign, newest edit first. Section key `notes`. */
export interface NotesSection {
  notes: Note[]
}

/**
 * The campaign's city, or all-null when it has none — which is why every field is
 * nullable rather than the section being absent. Section key `city`.
 */
export interface CitySection {
  id: number | null
  name: string | null
  description: string | null
  theme: string | null
  factionCount: number
  locationCount: number
}

/** A character's membership in a faction. */
export interface FactionMember {
  id: number
  characterId: number
  characterName: string | null
  portraitUrl: string | null
  role: Schema['MemberRole']
}

/**
 * A directed edge to another faction. The store is one-directional and the API keeps
 * it that way; the target's name ships alongside the id, so rendering one faction
 * needs no lookup table.
 */
export interface FactionRelationship {
  id: number
  relatedFactionId: number
  relatedFactionName: string | null
  type: Schema['RelationshipType']
}

/** One faction with its members and outbound relationships. */
export interface Faction {
  id: number
  name: string
  description: string
  goal: string
  members: FactionMember[]
  relationships: FactionRelationship[]
}

/** Every faction in the city, by name. Section key `factions`. */
export interface FactionsSection {
  factions: Faction[]
}

/** A character associated with a location, plus the GM's note about them being there. */
export interface LocationCharacter {
  id: number
  characterId: number
  characterName: string | null
  portraitUrl: string | null
  notes: string
}

/** An encounter the GM has planned at a location. */
export interface LocationEncounter {
  id: number
  name: string
  description: string
}

/** A gallery image. Read-only until Stage 8 brings uploads. */
export interface LocationImage {
  id: number
  type: Schema['LocationImageType']
  isMain: boolean
  title: string | null
  description: string | null
  url: string | null
  urlSmall: string | null
  urlThumb: string | null
}

/** One location with its people, planned encounters and images. */
export interface CampaignLocation {
  id: number
  name: string
  description: string
  imageUrl: string | null
  battleMapImageUrl: string | null
  characters: LocationCharacter[]
  plannedEncounters: LocationEncounter[]
  images: LocationImage[]
}

/** Every location in the city, by name. Section key `locations`. */
export interface LocationsSection {
  locations: CampaignLocation[]
}

// ---------------------------------------------------------------------------
// The sheet
// ---------------------------------------------------------------------------

export type CampaignSummary = Schema['CampaignSummaryDto']
export type AvailableUser = Schema['UserSummaryDto']

/**
 * A campaign, as this client holds it.
 *
 * Two reads populate the same shape with different sections — `GET /campaigns/{id}`
 * brings core, players, characters, notes and the city stub; `GET /campaigns/{id}/city`
 * brings city, factions and locations. They are cached separately (the world graph is
 * deep and most opens never show it), which is why `sections` is `Partial`: which keys
 * are present is a property of *which read* filled the entry, not of the campaign.
 */
export interface CampaignSheet {
  campaignId: number
  version: number
  isGameMaster: boolean
  isPlayer: boolean
  sections: Partial<CampaignSections>
}

/**
 * Narrows the wire DTO into the typed sheet. The single place the untyped `sections`
 * dictionary is given a type, so also the only place to look when a section reads as
 * `undefined` that should not.
 */
export function toCampaignSheet(dto: Schema['CampaignSheetDto']): CampaignSheet {
  if (dto.campaignId === undefined) {
    throw new Error('The API returned a campaign sheet with no id.')
  }

  return {
    campaignId: dto.campaignId,
    version: dto.version ?? 0,
    isGameMaster: dto.isGameMaster ?? false,
    isPlayer: dto.isPlayer ?? false,
    sections: (dto.sections ?? {}) as Partial<CampaignSections>,
  }
}

// ---------------------------------------------------------------------------
// Presentation helpers — layout only. Nothing here derives a game value.
// ---------------------------------------------------------------------------

/** True when the campaign has a city at all. The stub arrives all-null when it does not. */
export function hasCity(city: CitySection | undefined): boolean {
  return city !== undefined && city.id !== null
}

/**
 * The Faces of the city, per design spec §5.4 — the campaign roster, filtered by a
 * search term.
 *
 * Filtering happens here rather than server-side because, unlike the character
 * roster, the campaign's cast arrives whole inside the sheet: there is no paged
 * roster endpoint to push a `search` at, and a section is all-or-nothing.
 */
export function matchingCharacters(
  characters: readonly CampaignCharacter[],
  search: string,
): CampaignCharacter[] {
  const term = search.trim().toLowerCase()
  if (term === '') return [...characters]
  return characters.filter((character) => character.name.toLowerCase().includes(term))
}
